// ABOUTME: Super-admin panel for issuing a scope-limited agent token and copying it once, since the token is never retrievable afterwards.
import { IssuedToken, serviceAccountsApi } from '../../api/serviceAccountsApi';
import './ServiceAccountToken.css';
import { AxiosError } from 'axios';
import { useState } from 'react';

const DEFAULT_ACCOUNT_ID = 'cs-agent';

const errorMessage = (error: unknown, fallback: string): string =>
  (error as AxiosError<{ error?: string }>)?.response?.data?.error ?? fallback;

export const ServiceAccountToken = () => {
  const [accountId, setAccountId] = useState(DEFAULT_ACCOUNT_ID);
  const [issued, setIssued] = useState<IssuedToken | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isIssuing, setIsIssuing] = useState(false);
  const [copied, setCopied] = useState(false);

  const issue = async () => {
    setIsIssuing(true);
    setError(null);
    setCopied(false);
    try {
      setIssued(await serviceAccountsApi.issueToken(accountId.trim()));
    } catch (caught) {
      setIssued(null);
      setError(errorMessage(caught, 'Could not issue a token'));
    } finally {
      setIsIssuing(false);
    }
  };

  const copy = async () => {
    if (!issued) return;
    try {
      await navigator.clipboard.writeText(issued.token);
      setCopied(true);
    } catch {
      // Clipboard access is denied in some browser contexts; the token is
      // selectable in the field either way, so this is not worth an error state.
      setCopied(false);
    }
  };

  return (
    <div className="card service-account-token">
      <h2 className="trustflight-heading card-title">Agent tokens</h2>
      <p className="service-account-token-intro">
        Issues a read-only <code>cs:read</code> token for an automated agent.
        The token is signed by this environment, so it works here and nowhere
        else. It is shown once and never stored in readable form — if it is
        lost, issue another.
      </p>

      <div className="service-account-token-form">
        <label htmlFor="service-account-id">Account id</label>
        <input
          id="service-account-id"
          value={accountId}
          onChange={(event) => setAccountId(event.target.value)}
          placeholder={DEFAULT_ACCOUNT_ID}
        />
        <button
          className="button-primary"
          onClick={issue}
          disabled={isIssuing || accountId.trim().length === 0}
        >
          {isIssuing ? 'Issuing…' : 'Issue token'}
        </button>
      </div>

      {error && <p className="service-account-token-error">{error}</p>}

      {issued && (
        <div className="service-account-token-result">
          <div className="service-account-token-meta">
            <span>
              <strong>{issued.id}</strong> · {issued.scope} · expires in{' '}
              {issued.expiresIn}
            </span>
            <button className="button-secondary subtle" onClick={copy}>
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
          <textarea
            className="service-account-token-value"
            readOnly
            rows={4}
            value={issued.token}
            onFocus={(event) => event.currentTarget.select()}
          />
          <p className="service-account-token-revoke">{issued.revoke}</p>
        </div>
      )}
    </div>
  );
};
